import { useState, useRef } from 'react'
import { Form } from 'react-final-form'
import type { FormApi } from 'final-form'
import { makeStyles } from '@material-ui/core/styles'
import { Box } from '@material-ui/core'
import AgendaDetailForm from './AgendaDetailForm'
import MeetingParticipantsForm from './MeetingParticipantsForm'
import MeetingAgendaItemsForm from './MeetingAgendaItemsForm'
import arrayMutators from 'final-form-arrays'
import MyButton from '../../../control/button/button'
import { buildAgendaPayload, hasAtLeastOneAttendance } from './utils'
import { AgendaRequest } from './agendaTypes'
import { useSettingAgendas } from '../../../services/um/useSettingAgendas'
import useLoading from '../../../control/loading/useLoading'
import { useHistory } from 'react-router-dom'
import useTranslate from '../../../i18n/useTranslate'
import Dialog, { DialogType } from '../../../control/dialog/dialog'
import useAuthenStore from '../../../stores/authen/authenStore'

type SubmitIntent = 'draft' | 'submit'
type DialogRef = { openDialog?: () => void }

const initialValues: Partial<AgendaRequest> = {
    participants: {
        // เฉพาะกลุ่มที่บังคับกรอก (required + minItems 1) เท่านั้นที่ตั้งแถวเปล่าไว้
        // กลุ่มที่ไม่บังคับถ้าตั้งแถวเปล่าไว้ ผู้ใช้ที่ไม่กรอกจะได้แถวว่างติดไปในฐานข้อมูล
        // แล้วไปโผล่ในรายงานการประชุมเป็น "1. <ว่าง>  กรรมการ"
        attendances: [{ sendMail: true }],
        onLeaves: [],
        externalAttendances: [],
        presidents: [{ sendMail: true }],
        vicePresidents: [{ sendMail: true }],
        secretaries: [{ sendMail: true }],
    },
    topics: { agendas: [{ topicName: '', topicDescription: '', projects: [], children: [] }] },
}

const CreateAgendaTab = () => {
    const authenStore = useAuthenStore()
    const history = useHistory()
    const classes = useStyles()
    const t = useTranslate()
    const loadingHook = useLoading()
    const { upsertAgendas } = useSettingAgendas()

    const submitIntentRef = useRef<SubmitIntent>('draft')
    const formRef = useRef<FormApi<any> | null>(null)
    const dialogRef = useRef<DialogRef | null>(null)

    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm'>>()

    const onRefDialog = (func: Record<string, Function>) => {
        dialogRef.current = func as unknown as DialogRef
    }

    const onSubmit = async (values: any) => {
        loadingHook.loadingOpen()
        let navigated = false
        try {
            const isDraft = submitIntentRef.current === 'draft'
            const payload = { ...buildAgendaPayload(values), isDraft }
            const res = await upsertAgendas(String(authenStore?.profile?.irb), payload)
            if (res?.message === 'duplicated agenda_no') {
                setConfigDialog({
                    title: t.get('ALERT.TITLE.NOTIFICATION'),
                    content: t.get('ALERT.CONTENT.DUPLICATED_AGENDA_NO'),
                    type: 'alert-message',
                })
                dialogRef.current?.openDialog?.()
                return
            }

            const id = res.result?.id
            if (!id) return
            navigated = true
            history.push(`/settingAgendas/${id}/edit`)
        } finally {
            // อย่าพึ่งปิดเพราะเน็ตช้าจะมีจังหวะจอขาวก่อน edit จะ rendered
            if (!navigated) loadingHook.loadingClose()
            // loadingHook.loadingClose()
        }
    }

    const revealErrors = (form: FormApi<any>) => {
        form.batch(() => {
            form.getRegisteredFields().forEach((name) => form.blur(name))
        })
        form.submit()
    }

    const openConfirmValidated = (intent: SubmitIntent, form: FormApi<any>) => {
        const state = form.getState()

        if (intent === 'draft') {
            submitIntentRef.current = 'draft'
            void onSubmit(state.values)
            return
        }

        if (state.hasValidationErrors) {
            revealErrors(form)
            return
        }
        if (intent === 'submit' && !hasAtLeastOneAttendance(state.values)) {
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('UM.SETTING_AGENDA.PARTICIPANTS.REQUIRE_ATTENDANCE'),
                type: 'alert-message',
            })
            dialogRef.current?.openDialog?.()
            return
        }

        submitIntentRef.current = intent
        setConfigDialog({
            title: t.get('ALERT.TITLE.NOTIFICATION'),
            content: t.get('ALERT.CONTENT.NEED_TO_SAVE'),
            type: 'alert-confirm',
            onClickConfirm: () => formRef.current?.submit(),
        })
        dialogRef.current?.openDialog?.()
    }

    return (
        <Form
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            onSubmit={onSubmit}
            validate={() => {
                return {}
            }}
            render={({ form }) => {
                formRef.current = form
                return (
                    <>
                        <Box component="form" display="flex" flexDirection="column" style={{ gap: 20 }}>
                            <AgendaDetailForm />
                            <MeetingParticipantsForm prefix="participants" />
                            <MeetingAgendaItemsForm prefix="topics" />

                            <Box className={classes.pageFooter}>
                                <MyButton
                                    className={classes.footerButton}
                                    variant="contained"
                                    onClick={() => openConfirmValidated('draft', form)}
                                >
                                    {t.get('UM.SETTING_AGENDA.BUTTON.SAVE_DRAFT')}
                                </MyButton>

                                <div className={classes.rightActions}>
                                    <MyButton
                                        className={classes.footerButton}
                                        variant="outlined"
                                        onClick={() => {
                                            history.push('/settingAgendas')
                                        }}
                                    >
                                        {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                    </MyButton>

                                    <MyButton
                                        className={classes.footerButton}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => openConfirmValidated('submit', form)}
                                    >
                                        {t.get('UM.SETTING_AGENDA.BUTTON.SAVE_AND_REQUEST_APPROVAL')}
                                    </MyButton>
                                </div>
                            </Box>
                        </Box>

                        <Dialog onRef={onRefDialog} {...configDialog} />
                    </>
                )
            }}
        />
    )
}

const useStyles = makeStyles((theme) => ({
    root: { display: 'flex', maxWidth: 1320, margin: '24px auto', padding: theme.spacing(0, 2), width: '100%' },
    contentWrap: { flexGrow: 1 },
    layout: { display: 'grid', gridTemplateColumns: 'auto 1fr', width: '100%' },
    pageFooter: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    rightActions: {
        marginLeft: 'auto',
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            flexDirection: 'column',
        },
    },
    footerButton: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}))

export default CreateAgendaTab
